import { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { InternalPage } from "../../../components/primitives/layout/InternalPage";
import { SectionTitle } from "../../../components/primitives/data-display/SectionTitle";
import { Section } from "../../../components/primitives/layout/Section";
import { Panel } from "../../../components/primitives/surfaces/Panel";
import { Statistic } from "../../../components/primitives/data-display/Statistic";
import { gaps } from "../../../components/primitives/spacing";
import { useClaimVerificationTrace } from "./useClaimVerificationTrace";
import { VerificationTracePanel } from "../../../components/domain/data-display/VerificationTracePanel";
import { DocumentSelectionTracePanel } from "../../../components/domain/data-display/DocumentSelectionTracePanel";
import { InvestigationsHeader } from "../InvestigationsHeader";
import { formatStoredClaimKind } from "../../../types/claims";
import type { VerificationTiming } from "../../../types/claims";

interface LookupFormProps {
  title: string;
  description: string;
  fieldLabel: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
}

function LookupForm({
  title,
  description,
  fieldLabel,
  value,
  onChange,
  onSubmit,
  loading,
}: LookupFormProps) {
  return (
    <Stack gap={gaps.tight} sx={{ maxWidth: 700 }}>
      <SectionTitle>{title}</SectionTitle>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      <Stack
        component="form"
        direction="row"
        gap={gaps.snug}
        onSubmit={onSubmit}
        sx={{ mt: gaps.snug }}
      >
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          fullWidth
          inputProps={{ spellCheck: false, style: { fontFamily: "monospace" } }}
        />
        <Button type="submit" variant="contained" disabled={loading || !value.trim()}>
          {loading ? <CircularProgress size={20} /> : "Inspect"}
        </Button>
      </Stack>
    </Stack>
  );
}

const STEPS = [
  { key: "embed_ms", label: "embed", color: "#5c9cf5" },
  { key: "search_ms", label: "search", color: "#9b59b6" },
  { key: "rerank_ms", label: "rerank", color: "#e67e22" },
  { key: "judge_ms", label: "judge", color: "#e74c3c" },
] as const;

function TimingPanel({ timing }: { timing: VerificationTiming }) {
  const stepMs = STEPS.map((s) => ({ ...s, ms: timing[s.key] }));
  const total = timing.total_ms || 1; // guard against 0 for bar widths

  const sortedSources = [...timing.judge_sources].sort((a, b) => b.wall_ms - a.wall_ms);

  return (
    <Stack gap={2}>
      <Panel>
        <Stack gap={2}>
          {/* Step stats */}
          <Stack direction="row" gap={3} flexWrap="wrap">
            {stepMs.map(({ key, label, ms }) => (
              <Statistic key={key} label={label} mono>
                {ms.toLocaleString()}
                <Typography component="span" variant="caption" color="text.secondary">
                  {" "}
                  ms
                </Typography>
              </Statistic>
            ))}
            <Divider orientation="vertical" flexItem />
            <Statistic label="total" mono>
              {timing.total_ms.toLocaleString()}
              <Typography component="span" variant="caption" color="text.secondary">
                {" "}
                ms
              </Typography>
            </Statistic>
          </Stack>

          {/* Proportional bar */}
          <Stack gap={0.5}>
            <Stack direction="row" sx={{ height: 10, borderRadius: 1, overflow: "hidden" }}>
              {stepMs.map(({ key, label, ms, color }) => (
                <Tooltip key={key} title={`${label}: ${ms}ms`} placement="top" arrow>
                  <span
                    style={{
                      display: "block",
                      width: `${(ms / total) * 100}%`,
                      backgroundColor: color,
                      minWidth: ms > 0 ? 2 : 0,
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
            <Stack direction="row" gap={1.5} flexWrap="wrap">
              {stepMs.map(({ key, label, color }) => (
                <Stack key={key} direction="row" alignItems="center" gap={0.5}>
                  <span
                    style={{
                      display: "block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          {/* Provider / model */}
          <Typography variant="caption" color="text.disabled" fontFamily="monospace">
            {timing.provider} / {timing.model}
          </Typography>
        </Stack>
      </Panel>

      {/* Per-source judge timing */}
      {sortedSources.length > 0 && (
        <Panel>
          <Stack gap={1}>
            <Typography variant="caption" color="text.secondary">
              Per-source judge timing ({sortedSources.length} sources, slowest first)
            </Typography>
            {sortedSources.map((s) => (
              <Stack
                key={s.source_id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="caption"
                  fontFamily="monospace"
                  color="text.secondary"
                  noWrap
                  sx={{ maxWidth: 320 }}
                >
                  {s.source_id}
                </Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                  <span
                    style={{
                      display: "block",
                      width: `${Math.max((s.wall_ms / (sortedSources[0].wall_ms || 1)) * 120, 2)}px`,
                      height: 4,
                      backgroundColor: "#e74c3c",
                      borderRadius: 2,
                      opacity: 0.6,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontFamily="monospace"
                    color="text.primary"
                    sx={{ minWidth: 60, textAlign: "right" }}
                  >
                    {s.wall_ms.toLocaleString()} ms
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Panel>
      )}
    </Stack>
  );
}

/** Internal page — inspect the full computation trace for any verified claim. */
export default function ClaimVerificationTrace() {
  const [input, setInput] = useState("");
  const { data, isLoading, error, lookup } = useClaimVerificationTrace();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (input.trim()) void lookup(input.trim());
  }

  return (
    <InternalPage width="full">
      <InvestigationsHeader />
      <LookupForm
        title="Claim verification trace"
        description="Paste the verification event ID (UUID from the verify URL) to inspect the full verdict trace."
        fieldLabel="Verification event ID"
        value={input}
        onChange={setInput}
        onSubmit={handleSubmit}
        loading={isLoading}
      />

      {error && <Alert severity="error">{error}</Alert>}

      {data && (
        <Stack gap={gaps.base}>
          <Section title="Claim">
            <Panel>
              <Stack gap={gaps.snug}>
                <Typography variant="body2">{data.claim.canonical}</Typography>
                {data.claim.properties ? (
                  <Stack direction="row" gap={3} flexWrap="wrap">
                    <Statistic label="domain" mono>
                      {data.claim.properties.domain}
                    </Statistic>
                    <Statistic label="kind" mono>
                      {formatStoredClaimKind(data.claim.properties) ?? "unclassified"}
                    </Statistic>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No domain or kind stored: this claim was stored before they were assigned, and
                    is labelled the next time it is extracted.
                  </Typography>
                )}
              </Stack>
            </Panel>
          </Section>

          <Section title="Verdict">
            <Panel>
              <Stack direction="row" gap={3} flexWrap="wrap">
                <Statistic label="verdict">{data.claim_verdict.verdict}</Statistic>
                <Statistic label="signal" mono>
                  {data.claim_verdict.signal.toFixed(3)}
                </Statistic>
                <Statistic label="evidence (total / inconclusive)" mono>
                  {data.claim_verdict.evidence_count.total} /{" "}
                  {data.claim_verdict.evidence_count.inconclusive}
                </Statistic>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    cached at
                  </Typography>
                  <Typography variant="body2">
                    {new Date(data.cached_at).toLocaleString()}
                  </Typography>
                </Stack>
              </Stack>
            </Panel>
          </Section>

          {data.trace?.timing ? (
            <Section title="Timing">
              <TimingPanel timing={data.trace.timing} />
            </Section>
          ) : null}

          <Divider />

          {data.trace ? (
            <>
              <Section title="Document selection">
                {data.trace.document_selection ? (
                  <DocumentSelectionTracePanel
                    trace={data.trace.document_selection}
                    judgments={data.trace.source_judgment_traces ?? undefined}
                  />
                ) : (
                  <Alert severity="warning">
                    No document-selection trace available — this verification was computed before
                    that stage was traced.
                  </Alert>
                )}
              </Section>

              <Section title="Computation trace">
                <VerificationTracePanel trace={data.trace} evidence={data.evidence} />
              </Section>
            </>
          ) : (
            <Alert severity="warning">
              No trace available — this verification was computed before trace storage was
              introduced.
            </Alert>
          )}
        </Stack>
      )}
    </InternalPage>
  );
}
